import graphql__31fa1ed32578bdf7f8b5 from "./__generated__/MapContextProvider_map.graphql.js";import { createContext, useRef, useEffect, useContext, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { useSelector, useDispatch } from "react-redux";

import { ResourcesListProvider } from "../ResourcesList/resourcesList";
import { getDisplayName } from "../Utils/getDisplayName";

import { initializeMap } from "../../reducers/map/initialize";
import { useRoutesContext } from "../Routes/RoutesContext";
import { pull, lowerFirst, upperFirst } from "lodash";

const MAP_KEY_PREFIX = "map";

const MAP_TYPE_CONTRIBUTIONS_MAP = "participativeModule";
const MAP_TYPE_USERS_MAP = "usersMap";
const MAP_TYPE_INFORMATIVE_MAP = "informativeMap";
const MAP_TYPE_CITY_WALK = "cityWalk";

export const MAP_ITEM_TYPE_ELEMENT = "element";

const MapContext = createContext();

// The map HOC is composed of 2 levels of wrapper components which
// roles are :
//
//   - MapContainer : Generate a `mapKey` on instanciation and
//     provide it down to the actual Map component
//
//   - Map : Wrap the provided `WrappedComponent` and connect
//     it with Redux to initialize the map state and provide the search and
//     filtering data to the WrappedComponent so it can use the state without
//     directly connecting to Redux.
//
// Make the `map` prop default to `null` to allow specific components to render the
// MapContextProvider without providing a target map, which just wont provide a way for
// sub components to link to the map. For example, event maps won't render a link to the
// event map, as the event map is just part of the event page, and is displayed on the
// event page only.
//
const MapContextProvider = ({ map = null, ...props }) => {
  const { children } = props;

  const { __typename, slug: mapSlug } = useFragment(MapContextProvider_map, map) || {};
  const mapType = mapTypeForGraphQLTypename(__typename);

  const mapKeyRef = useRef();

  // Build the mapKey that will be used by child components to access the map
  // state in the redux store
  if (!mapKeyRef.current) {
    const time = new Date().getTime();
    const random = Math.random(100000000);

    mapKeyRef.current = `${MAP_KEY_PREFIX}:${time}:${random}`;
  }

  const mapKey = mapKeyRef.current;

  const dispatch = useDispatch();
  // Initialize the map when the component is mounted
  useEffect(() => {
    dispatch(initializeMap(mapKey));
  }, [dispatch, mapKey]);

  const isMapInitialized = useSelector(({ map }) => map[mapKey]);

  const { pathFor } = useRoutesContext();
  const mapSlugParamName = [mapType, "Slug"].join("");

  // Provide a `mapPathFor` function through the context to allow child component to
  // generate map-namespaced routes without having to care about map type polymorphism
  //
  // For example, for an informativeMap with a slug of "carte" :
  //
  //     const eventSlug = "reunion";
  //     const { mapPathFor } = useMapContext();
  //     const eventPath = mapPathFor("event", { eventSlug })
  //
  //     // calls : pathFor("informativeMapEvent", {
  //                  informativeMapSlug: "carte",
  //                  eventSlug: "reunion"
  //                })
  //     // => "/cartes-informatives/carte/evenements/reunion"
  //
  // Note that the sub map resources routes should exist in the routes context for it to
  // work, it only acts as an abstract shortcut for existing static routes.
  //
  const mapPathFor = useCallback(
    (subRouteName, params = {}) => {
      const routeName = [mapType, upperFirst(subRouteName)].join("");
      return pathFor(routeName, { [mapSlugParamName]: mapSlug, ...params });
    },
    [mapSlug, mapType, pathFor, mapSlugParamName]
  );

  const mapPath = pathFor(mapType, { [mapSlugParamName]: mapSlug });

  // Map clicked handlers allow child elements to register handlers for the map clicked
  // event. It's a feature that already exists in react-leaflet v3 but as long as we're
  // using the v2 we need to implement it ourselves. This is currently used for map
  // elements to listen map clicks and unselect themselves when the map is clicked.
  //
  // The below methods are used to register and unregister those handlers, which are then
  // triggered by the MapBase component through the `onMapClicked` function when the
  // leaflet map is clicked.
  //
  // Note that map clicks doesn't include map elements click, it only triggers when the
  // map background is clicked, which is perfect for our current use case.
  const mapClickedHandlersRef = useRef([]);

  const registerMapClickedHandler = useCallback(
    handler => mapClickedHandlersRef.current.push(handler),
    []
  );

  const unregisterMapClickedHandler = useCallback(
    handler => pull(mapClickedHandlersRef.current, handler),
    []
  );

  // This callback is provided to the MapBase component to trigger the registered map
  // click event handlers without needing to be manage them.
  const onMapClicked = useCallback(
    e => mapClickedHandlersRef.current.forEach(handler => handler(e)),
    []
  );

  // Do not render child components before the map is actually initialized
  if (!isMapInitialized) return null;

  const contextValue = {
    mapKey,
    mapType,
    mapSlug,
    mapPath,
    mapPathFor,
    onMapClicked,
    registerMapClickedHandler,
    unregisterMapClickedHandler
  };

  return (
    <ResourcesListProvider>
      <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
    </ResourcesListProvider>
  );
};

const MapContextProvider_map = graphql__31fa1ed32578bdf7f8b5;

export default MapContextProvider;

export const useMapContext = () => {
  return useContext(MapContext) || {};
};

export const useMapType = () => {
  const { mapType } = useMapContext();
  const isContributionsMap = mapType === MAP_TYPE_CONTRIBUTIONS_MAP;
  const isUsersMap = mapType === MAP_TYPE_USERS_MAP;
  const isInformativeMap = mapType === MAP_TYPE_INFORMATIVE_MAP;
  const isCityWalk = mapType === MAP_TYPE_CITY_WALK;

  return { isContributionsMap, isUsersMap, isInformativeMap, isCityWalk };
};

export const mapTypeForGraphQLTypename = typename =>
  typename === "VisualizationMap" ? MAP_TYPE_CONTRIBUTIONS_MAP : lowerFirst(typename);

// This is a legacy HOC for consuming the map context, and is needed because a few map
// components are still not migrated to using hooks.
//
export const withMapContext = WrappedComponent => {
  const WithMapContext = props => {
    const contextProps = useMapContext();

    return (
      <WrappedComponent {...props} {...contextProps}>
        {props.children}
      </WrappedComponent>
    );
  };

  WithMapContext.displayName = `WithMapContext(${getDisplayName(WrappedComponent)})`;

  return WithMapContext;
};
